import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { optimizeImage } from "../../utils/imageUtils";
import {
  CollageTemplateType,
  PAPER_SIZES,
  PaperSize,
} from "../../utils/constants";
import CollageGrid from "./CollageGrid";
import UploadButton from "../Shared/UploadButton";
import ExportButtons from "./ExportButtons";
import Spinner from "../Shared/Spinner";

interface CollagePreviewProps {
  template: CollageTemplateType;
  paperSize: PaperSize;
}

const MM_TO_PX = 3.78;

const CollagePreview: React.FC<CollagePreviewProps> = ({
  template,
  paperSize,
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const printableRef = useRef<HTMLDivElement | null>(null);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const size = PAPER_SIZES[paperSize];

  const getCellCount = (t: CollageTemplateType) => {
    switch (t) {
      case "grid1x1":
        return 1;
      case "grid1x2":
        return 2;
      case "grid2x1":
        return 2;
      case "grid2x2":
        return 4;
      case "grid3x1":
        return 3;
      case "grid3x2":
        return 6;
      case "grid3x3":
        return 9;
      default:
        return images.length || 1;
    }
  };

  useEffect(() => {
    const count = getCellCount(template);
    setImages((prev) => {
      const next = [...prev];
      next.length = count;
      for (let i = 0; i < count; i++) if (!next[i]) next[i] = "";
      return next;
    });
  }, [template]);

  const handleBulkUpload = async (files: FileList | null) => {
    if (!files) return;
    setIsLoading(true);
    try {
      const arr = Array.from(files);
      const optimized = await Promise.all(arr.map((f) => optimizeImage(f)));
      setImages((prev) => {
        const next = [...prev];
        for (let i = 0; i < optimized.length && i < next.length; i++)
          next[i] = optimized[i];
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellUpload = async (index: number, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setIsLoading(true);
    try {
      const data = await optimizeImage(fileList[0]);
      setImages((prev) => {
        const next = [...prev];
        next[index] = data;
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCell = (index: number) => {
    setImages((prev) => {
      const next = [...prev];
      next[index] = "";
      return next;
    });
  };

  const handleReset = () => setImages((prev) => prev.map(() => ""));

  const handleExport = async (type: "pdf" | "image") => {
    if (!printableRef.current) return;
    setIsLoading(true);
    try {
      const pxWidth = Math.round(size.width * MM_TO_PX);
      const pxHeight = Math.round(size.height * MM_TO_PX);

      const clone = printableRef.current.cloneNode(true) as HTMLElement;
      clone.style.width = pxWidth + "px";
      clone.style.height = pxHeight + "px";
      clone.style.position = "absolute";
      clone.style.top = "-9999px";

      // Remove grid lines and buttons in export
      clone.querySelectorAll(".preview-overlay, .delete-button").forEach(el => el.remove());

      document.body.appendChild(clone);

      await Promise.all(
        Array.from(clone.querySelectorAll("img")).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve(true);
              else img.addEventListener("load", () => resolve(true));
            })
        )
      );

      const canvas = await html2canvas(clone, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 2,
        width: pxWidth,
        height: pxHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      document.body.removeChild(clone);

      if (type === "pdf") {
        const orientation = size.width > size.height ? "landscape" : "portrait";
        const pdf = new jsPDF(orientation as any, "mm", [size.width, size.height]);
        pdf.addImage(imgData, "PNG", 0, 0, size.width, size.height);
        pdf.save(`collage_${paperSize}.pdf`);
      } else {
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `collage_${paperSize}.png`;
        link.click();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col xl:flex-row gap-6 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50 rounded-lg">
          <Spinner />
        </div>
      )}

      {/* LEFT SIDE: PREVIEW */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <UploadButton multiple onChange={(e) => handleBulkUpload(e.target.files)} label="Upload Images (bulk)" />
          <button onClick={handleReset} className="px-3 py-1 text-white bg-red-500 rounded-md text-sm">Reset All</button>
        </div>

        <div className="w-full mx-auto max-w-[800px]">
          <div
            ref={printableRef}
            className="bg-white rounded-lg overflow-hidden border border-gray-100"
            style={{ aspectRatio: `${size.width} / ${size.height}`, width: "100%" }}
          >
            <CollageGrid
              template={template}
              images={images}
              onCellClick={(i) => inputRefs.current[i]?.click()}
              previewMode={true}
              onDeleteCell={handleDeleteCell}
            />
          </div>
        </div>

        {/* Hidden Inputs */}
        <div style={{ display: "none" }}>
          {images.map((_, idx) => (
            <input
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              type="file"
              accept="image/*"
              onChange={(e) => handleCellUpload(idx, e.target.files)}
            />
          ))}
        </div>
      </div>

      {/* RIGHT SIDE: SETTINGS PANEL */}
      <aside className="w-full xl:w-80 flex-shrink-0">
        <div className="sticky top-4 space-y-5">
          <div className="bg-white border rounded-lg p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview Settings</h3>
            <p className="text-sm text-gray-600 mb-1">
              Paper: <strong>{paperSize} ({size.width}×{size.height} mm)</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Template: <strong>{template}</strong>
            </p>
            <ExportButtons onExport={handleExport} disabled={isLoading} />
          </div>
        </div>
      </aside>
    </div>
  );
};

export default CollagePreview;
